import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const defaultRole = sp.get("role") === "partner" ? "partner" : "customer";

  const [role, setRole] = useState(defaultRole);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!acceptedTerms) { toast.error("Please accept the Terms & Privacy Policy to continue"); return; }
    setLoading(true);
    try {
      const u = await register({ ...form, role });
      toast.success("Account created");
      navigate(u.role === "partner" ? "/dashboard/partner" : "/dashboard/customer");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <div className="mx-auto max-w-3xl px-6 py-16">
        <div className="text-xs uppercase tracking-[0.2em] font-medium text-primary mb-4">Create account</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mb-2">Join VayuCheck</h1>
        <p className="text-muted-foreground mb-10">Pick your role to get started.</p>

        <div className="grid grid-cols-2 gap-px bg-border mb-10" data-testid="role-selector">
          <button
            type="button"
            data-testid="role-customer"
            onClick={() => setRole("customer")}
            className={`p-8 text-left transition-colors duration-200 ${role === "customer" ? "bg-primary text-primary-foreground" : "bg-secondary hover:bg-secondary/80 text-foreground"}`}
          >
            <div className="text-xs uppercase tracking-[0.2em] mb-3 opacity-80">Vehicle owner</div>
            <div className="font-display text-2xl font-bold">Customer</div>
            <div className="text-sm mt-2 opacity-80">Book PUC tests for your vehicles.</div>
          </button>
          <button
            type="button"
            data-testid="role-partner"
            onClick={() => setRole("partner")}
            className={`p-8 text-left transition-colors duration-200 ${role === "partner" ? "bg-primary text-primary-foreground" : "bg-secondary hover:bg-secondary/80 text-foreground"}`}
          >
            <div className="text-xs uppercase tracking-[0.2em] mb-3 opacity-80">Test centre</div>
            <div className="font-display text-2xl font-bold">Partner</div>
            <div className="text-sm mt-2 opacity-80">List your PUC centre & get bookings.</div>
          </button>
        </div>

        <form onSubmit={submit} className="space-y-5 border border-border bg-card p-8">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Full name</Label>
              <Input data-testid="register-name" required value={form.name} onChange={setField("name")} className="rounded-lg bg-secondary border-border h-11 mt-2" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Phone</Label>
              <Input data-testid="register-phone" required value={form.phone} onChange={setField("phone")} className="rounded-lg bg-secondary border-border h-11 mt-2" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Email</Label>
            <Input data-testid="register-email" type="email" required value={form.email} onChange={setField("email")} className="rounded-lg bg-secondary border-border h-11 mt-2" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Password</Label>
            <Input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={setField("password")} className="rounded-lg bg-secondary border-border h-11 mt-2" placeholder="Min 6 characters" />
          </div>

          <Button data-testid="register-submit-btn" type="submit" disabled={loading || !acceptedTerms} className="w-full rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-12">
            {loading ? "Creating..." : `Create ${role} account`}
          </Button>

          <label className="flex items-start gap-3 text-sm text-muted-foreground select-none">
            <Checkbox
              data-testid="register-accept-terms"
              checked={acceptedTerms}
              onCheckedChange={(v) => setAcceptedTerms(!!v)}
              className="mt-0.5 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
            />
            <span>
              I agree to VayuCheck&apos;s{" "}
              <Link to="/terms" target="_blank" className="text-primary hover:underline font-medium">Terms &amp; Conditions</Link>
              {" "}and{" "}
              <Link to="/privacy" target="_blank" className="text-primary hover:underline font-medium">Privacy Policy</Link>.
            </span>
          </label>

          <p className="text-sm text-muted-foreground text-center">
            Already have an account? <Link to="/login" data-testid="link-login" className="text-primary hover:underline">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
